import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {cn} from "@/lib/utils";

export type KpiCardDensity = "default" | "compact";

type KpiCardProps = {
  label: string;
  icon?: React.ElementType;
  className?: string;
  density?: KpiCardDensity;
  selected?: boolean;
  onSelect?: () => void;
  disabled?: boolean;
  children: React.ReactNode;
};

/**
 * Shared shell for every KPI tile. Optional `onSelect` makes the card a
 * control (no route navigation).
 */
export function KpiCard({
  label,
  icon: Icon,
  className,
  density = "default",
  selected = false,
  onSelect,
  disabled = false,
  children,
}: KpiCardProps) {
  const isCompact = density === "compact";
  const interactive = Boolean(onSelect) && !disabled;

  const card = (
    <Card
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-pressed={interactive ? selected : undefined}
      aria-disabled={disabled || undefined}
      onClick={interactive ? onSelect : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect?.();
              }
            }
          : undefined
      }
      className={cn(
        "group/kpi-card flex h-full min-h-[8.5rem] flex-col overflow-hidden transition-colors",
        isCompact ? "gap-2 py-3" : "gap-3 py-4",
        interactive && "cursor-pointer",
        interactive && !selected && "hover:border-primary/25",
        selected && "ring-ring border-primary/40 ring-2",
        disabled && "pointer-events-none opacity-70",
        className,
      )}
    >
      <CardHeader
        className={cn(
          "flex flex-row items-start justify-between gap-3",
          isCompact ? "px-4 pb-0 pt-1 md:px-4" : undefined,
        )}
      >
        <CardTitle
          className={cn(
            "text-muted-foreground line-clamp-2 min-w-0 pr-1 font-semibold leading-tight",
            isCompact ? "text-sm" : "text-base",
          )}
        >
          {label}
        </CardTitle>
        {Icon ? (
          <span
            className={cn(
              "bg-muted text-muted-foreground flex shrink-0 items-center justify-center rounded-lg transition-colors group-hover/kpi-card:bg-primary/10 group-hover/kpi-card:text-primary",
              selected && "bg-primary/10 text-primary",
              isCompact ? "size-8" : "size-9",
            )}
          >
            <Icon className="size-4" />
          </span>
        ) : null}
      </CardHeader>
      <CardContent
        className={cn(
          "flex min-h-0 flex-1 flex-col",
          isCompact ? "gap-2 px-4 pb-3 pt-0 md:px-4" : "gap-3",
        )}
      >
        {children}
      </CardContent>
    </Card>
  );

  return card;
}
